// scrollUtils.js

/**
 * @returns {boolean} if browser supports options.passive in window.addEventListener()
 */
function getIsPassiveSupported() {
  // Reference: https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Safely_detecting_option_support
  let isPassiveSupported = false;

  if (typeof window !== 'undefined') {
    try {
      const options = Object.defineProperty({}, 'passive', {
        get() {
          // If browser reads this option, set isPassiveSupported to true
          isPassiveSupported = true;
          return true;
        },
      });

      // browser will read (if supported) options.passive after calling window.addEventListener()
      window.addEventListener('test', options, options);
      window.removeEventListener('test', options, options);
    } catch (err) {
      isPassiveSupported = false;
    }
  }

  return isPassiveSupported;
}

export const aggressive = getIsPassiveSupported() ? { passive: false } : false;

/**
 * @param {TouchEvent} e
 * @returns {number}
 */
export const getTouchY = e =>
  e.changedTouches ? e.changedTouches[0].clientY : 0;

/**
 * @param {HTMLElement} node
 * @returns {boolean}
 * */
const isScrollAllowedByStyles = node => {
  const styles = window.getComputedStyle(node);
  return (
    styles.overflowY !== 'hidden' && // not-not-scrollable
    !(styles.overflowY === styles.overflowX && styles.overflowY === 'visible') // scrollable
  );
};

/**
 * @param {HTMLElement} endTarget
 * @param {UIEvent} e
 * @param {number} sourceDelta
 * @return {bool} should cancel scroll.
 */
export const getShouldCancelScroll = (endTarget, e, sourceDelta) => {
  const delta = sourceDelta;
  // find scrollable target
  let target = e.target;
  const targetInLock = endTarget.contains(target);

  let shouldCancelScroll = false;
  const isDeltaPositive = delta > 0;

  let availableScroll = 0;
  let availableScrollTop = 0;

  do {
    const { scrollTop, scrollHeight, clientHeight } = target;

    const elementScroll = scrollHeight - clientHeight - scrollTop;
    if (scrollTop || elementScroll) {
      if (isScrollAllowedByStyles(target)) {
        availableScroll += elementScroll;
        availableScrollTop += scrollTop;
      }
    }

    target = target.parentNode;
  } while (
    // portaled content
    (!targetInLock && target !== document.body) ||
    // self content
    (targetInLock && (endTarget.contains(target) || endTarget === target))
  );

  if (isDeltaPositive && delta > availableScroll) {
    shouldCancelScroll = true;
  } else if (!isDeltaPositive && -delta > availableScrollTop) {
    shouldCancelScroll = true;
  }

  return shouldCancelScroll;
};
